import { createSelector } from '@reduxjs/toolkit';

/*
Reading the account's saved library.

One selector here carries the weight: `selectLibraryEnabled`. The Library's
limits are three keys in the plan catalog, and their PRESENCE is the feature
flag — the front end can ship every screen before the backend has a table,
because with no `saved_items_*` key anywhere the UI renders nothing at all. That
keeps FE and BE releases independent instead of needing to land together.
*/

/** The three tables that have a library, and the item_type each maps to. */
export const LIBRARY_TYPE_BY_TABLE = {
  locations: 'location',
  vehicles: 'vehicle',
  vehicle_types: 'vehicle_type',
};

/** The plans.json limit key for an item type. */
export const LIBRARY_LIMIT_KEYS = {
  location: 'saved_items_locations',
  vehicle: 'saved_items_vehicles',
  vehicle_type: 'saved_items_vehicle_types',
};

export const selectLibraryStatus = (state) => state?.library?.status || 'idle';
export const selectLibraryError = (state) => state?.library?.error || null;
export const selectLibraryItemsRaw = (state) => state?.library?.items || null;

/** Items of one type, or [] while nothing is loaded. Memoized per call site by
 *  the factory below — a fresh array every render would churn every consumer. */
export const selectLibraryItems = (itemType) => createSelector(
  [selectLibraryItemsRaw],
  (items) => (items || []).filter((item) => item?.item_type === itemType),
);

/** Every tag in use for a type, lowercase and sorted — the picker's filter
 *  chips and the save dialog's autocomplete both read this. Tags come back
 *  normalised from the server, so no case folding is needed here. */
export const selectLibraryTags = (itemType) => createSelector(
  [selectLibraryItems(itemType)],
  (items) => {
    const tags = new Set();
    items.forEach((item) => (item?.tags || []).forEach((tag) => tags.add(tag)));
    return [...tags].sort();
  },
);

export const selectLibraryCount = (itemType) => createSelector(
  [selectLibraryItems(itemType)],
  (items) => items.length,
);

/**
 * The plan's cap for a type: a number, or null for unlimited (or unknown).
 *
 * The user's own limits come from `/user_plan`. A free account has none there
 * (`user_limits` is null, and setUserLimits fills scenario limits from the free
 * template instead), so the library keys are absent and the Account page showed
 * "3 saved" where it should say "3 of 25 saved". When the user's limits don't
 * carry the key, the cap is read from the public plan catalog: the entry for the
 * user's plan, or the free one. A key that is present but null means unlimited
 * and is kept as null.
 *
 * The free entry is only a stand-in for an UNKNOWN plan (signed out, or the plan
 * hasn't loaded). If the catalog has the user's plan and that entry doesn't
 * carry the key, the answer is "unknown" (null), not the free cap: falling
 * through showed a professional account the free plan's number — a cap lower
 * than their real one, on a page that tells them how much they may save.
 */
export const selectLibraryLimit = (itemType) => (state) => {
  const key = LIBRARY_LIMIT_KEYS[itemType];
  if (!key) return null;
  const limits = state?.inputData?.present?.loginData?.limits;
  if (limits && key in limits) {
    const value = limits[key];
    return Number.isFinite(value) ? value : null;
  }
  const catalog = state?.ui?.planCatalog?.items;
  if (!Array.isArray(catalog)) return null;
  const plan = state?.inputData?.present?.loginData?.plan;
  const planEntry = plan ? catalog.find((item) => item?.tier === plan) : null;
  const entry = planEntry || catalog.find((item) => item?.tier === 'free');
  const value = entry?.limits?.[key];
  return Number.isFinite(value) ? value : null;
};

/** How many more of this type may be saved: a number, or null when unlimited
 *  or uncapped. */
export const selectLibraryRemaining = (itemType) => (state) => {
  const limit = selectLibraryLimit(itemType)(state);
  if (limit === null) return null;
  return Math.max(0, limit - selectLibraryItems(itemType)(state).length);
};

/**
 * Is the Library feature live for this build?
 *
 * True once ANY `saved_items_*` key appears in the user's limits or in the
 * public plan catalog. Both are read because they arrive on different paths:
 * the catalog is public and fetched for everyone, the user's limits need a
 * session. A signed-out visitor therefore still sees the feature exists, which
 * is the point — it is a reason to make an account.
 *
 * This is the whole release-decoupling mechanism: ship the UI whenever, and it
 * appears the day the backend publishes the keys.
 */
export const selectLibraryEnabled = (state) => {
  const keys = Object.values(LIBRARY_LIMIT_KEYS);

  const limits = state?.inputData?.present?.loginData?.limits;
  if (limits && keys.some((key) => key in limits)) return true;

  const catalog = state?.ui?.planCatalog?.items;
  if (Array.isArray(catalog)) {
    return catalog.some((plan) =>
      plan?.limits && keys.some((key) => key in plan.limits));
  }
  return false;
};
